import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import ActionableTranslation from 'tio-ui/components/actionable-translation';
import Button from 'tio-ui/components/button';
import GuidelinesModal from 'tio-employee/components/tas/course-cash/guidelines-modal';

interface CourseCashLegalSignature {
  Args: {
    /** Where this instance is mounted, e.g. "task-card" / "card-details". Both can
     * be on the page at once (the drawer opens over the panel), so the root test
     * hook carries this as its VALUE — `[data-test-course-cash-legal="task-card"]`
     * stays unambiguous, and child hooks are scoped through it. */
    context: string;
    /** Hide the guidelines link while leaving the issuer disclosure in place —
     * the drawer's Card Details tab suppresses it for a deactivated card, where
     * there is nothing left to spend and so no guidelines to follow. The
     * disclosure is not conditional: it documents who issued the card, which
     * stays true after deactivation. Defaults to shown. */
    showGuidelines?: boolean;
  };
  Element: HTMLDivElement;
}

const CELTIC_AUTHORIZED_USER_TERMS =
  'https://stripe.com/legal/issuing/celtic-authorized-user-terms';

/**
 * The "Guidelines for Use" link + the Stripe/Celtic Bank issuing disclosure that
 * accompany a CourseCa$h™ card wherever card details are shown — the drawer's
 * Card Details tab and the "Needs Your Attention" CourseCash task card.
 *
 * Extracted rather than duplicated: these are a legal pairing (the guidelines
 * the employee agreed to, and the issuer disclosure Stripe requires alongside
 * the card), so a copy or URL change has to land in both places at once.
 */
export default class CourseCashLegal extends Component<CourseCashLegalSignature> {
  @tracked showGuidelinesModal = false;

  get showGuidelines(): boolean {
    return this.args.showGuidelines ?? true;
  }

  toggleGuidelinesModal = () => {
    this.showGuidelinesModal = !this.showGuidelinesModal;
  };

  <template>
    <div class="tio-col gap-2" data-test-course-cash-legal={{@context}} ...attributes>
      {{#if this.showGuidelines}}
        <div>
          {{! `p-0` needs no important marker: spacing.css is @layer utilities
              and .tio-btn--md is @layer components, so the layer order alone
              beats the button's padding. The transparent background and
              absent shadow come from @appearance="minimal" itself. }}
          <Button
            @appearance="minimal"
            class="tio-anchor text-sm p-0"
            data-test-course-cash-guidelines-cta
            {{on "click" this.toggleGuidelinesModal}}
          >
            {{t "tas.course_cash_guidelines.button_label"}}
          </Button>
        </div>
      {{/if}}

      <div class="tio-fg-secondary text-xs" data-test-course-cash-issuer-terms>
        <ActionableTranslation
          @t="tas.card_holder.footer_text"
          @links={{hash
            termsOfUse=(hash
              href=CELTIC_AUTHORIZED_USER_TERMS
              t="tas.card_holder.footer_text_terms_link"
              target="_blank"
              rel="noopener noreferrer"
            )
          }}
        />
      </div>
    </div>

    <GuidelinesModal @isOpen={{this.showGuidelinesModal}} @onClose={{this.toggleGuidelinesModal}} />
  </template>
}
